import { CurrencyPipe, DOCUMENT } from '@angular/common';
import { DomSanitizer } from '@angular/platform-browser';
import {
  ApplicationRef,
  Directive,
  ElementRef,
  EmbeddedViewRef,
  EventEmitter,
  HostListener,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  SecurityContext,
  SimpleChanges,
  TemplateRef,
} from '@angular/core';
import { SafeHtmlPipe } from '@app/finxone-web-frontend/app/lib/pipes/SafeHtml.pipe';
import { getQueryParamValueByKey } from '@app/finxone-web-frontend/app/lib/utils/query-params.utils';
import { currencyCodeToFlag } from '@app/finxone-web-frontend/app/lib/utils/template/handlebar-helpers/currency-code-to-flag';
import { currencyToCircleFlag } from '@app/finxone-web-frontend/app/lib/utils/template/handlebar-helpers/currency-to-flag.utils';
import { DropdownMenuDirection } from '@finxone-platform/shared/sys-config-types';
import * as Handlebars from 'handlebars';
import { isArray } from 'lodash';
import { DropdownListingInterface } from '../accounts-context-dropdown-widget.component';

@Directive({
  // eslint-disable-next-line @angular-eslint/directive-selector
  selector: '[appAccountDropdown]',
  providers: [CurrencyPipe, SafeHtmlPipe],
})
export class AccountsContextDropdownWidgetDirective implements OnInit, OnDestroy, OnChanges {
  @Input() dropdownListing: DropdownListingInterface[] = [];
  @Input() dropdownTemplate = '';
  @Input() showFlag = true;
  @Input() isScrollable = true;
  @Input() visibleItemsCount = 5;
  @Input() isScrollPeekEnabled = false;
  @Input() dropdownMenuDirection = DropdownMenuDirection.AUTO;
  @Input() noRecordsTemplate = 'No data found';
  @Input() footerTemplate?: TemplateRef<unknown> | null = null;
  @Input() footerFloatingThreshold?: number | null = null;
  @Output()
  selectionChanged: EventEmitter<DropdownListingInterface> = new EventEmitter<DropdownListingInterface>();
  @Output() dropdownOpened: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() scrolled = new EventEmitter<void>();
  public dropdown: HTMLElement;
  public isOpen = false;
  private footerView?: EmbeddedViewRef<unknown> | null = null;
  private footerContainer?: HTMLElement | null = null;

  constructor(
    private el: ElementRef,
    private renderer: Renderer2,
    private currencyPipe: CurrencyPipe,
    @Inject(DOCUMENT) private document: Document,
    private appRef: ApplicationRef,
    private sanitizer: DomSanitizer,
    private safeHtmlPipe: SafeHtmlPipe,
  ) {}

  ngOnInit() {
    this.createDropdown();
    this.document.addEventListener('scroll', this.onScroll.bind(this), true);
    this.document.addEventListener('click', this.onDocumentClick);
  }

  onScroll(event: Event) {
    const element = event.target as HTMLElement;
    const scrollPercentage = (element.scrollTop / (element.scrollHeight - element.clientHeight)) * 100;
    if (scrollPercentage > 80) {
      this.scrolled.emit();
    }
    //only close when the list is scrollable, because the user would scroll the page to access the list
    //when scrolling is disabled
    if (this.isScrollable && this.isOpen && !this.dropdown.contains(event.target as Node)) {
      this.toggleDropdown();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['dropdownListing'] && this.dropdown) {
      this.clearDropdown();
      this.removeDropdownFromBody();
      this.createDropdown();

      // If the dropdown was already open when the list changed, reposition the newly created dropdown so it remains visible in the correct place.
      if (this.isOpen) {
        this.setPosition(true);
      }
    }
  }

  private updateDropdownContent(account: DropdownListingInterface): string {
    // Compile the Handlebars template
    const template = Handlebars.compile(this.dropdownTemplate);

    const model: any = { ...account };

    if (typeof account.totalBalance === 'string' && typeof account.currencyCode === 'string') {
      // Custom transformation for specific keys
      if (model.totalBalance && model.currencyCode) {
        model.totalBalance = this.currencyPipe.transform(
          model.totalBalance as string,
          model.currencyCode as string,
          'symbol-narrow',
        );
      }
    }

    // Render the template with the model
    const renderedTemplate = template(model);

    return renderedTemplate;
  }

  createAccountSection(account: DropdownListingInterface): HTMLElement {
    const section = this.renderer.createElement('section');
    this.renderer.addClass(section, 'd-flex');
    this.renderer.listen(section, 'click', () => {
      this.selectionChanged.emit(account);
      this.toggleDropdown();
    });
    this.renderer.addClass(section, 'justify-content-between');
    this.renderer.addClass(section, 'ml-2');

    const accountDiv = this.renderer.createElement('div');
    this.renderer.addClass(accountDiv, 'd-flex');
    this.renderer.addClass(accountDiv, 'items-center');
    this.renderer.addClass(accountDiv, 'w-100');

    const entry = currencyCodeToFlag.find((option: { country_code: string; currency_code: string }) => {
      return isArray(account.currency) ? false : option.currency_code === account.currency?.toUpperCase();
    });
    const img = this.renderer.createElement('img');
    if (entry) {
      this.renderer.setAttribute(img, 'src', `/assets/flags/${entry.country_code.toLowerCase()}.svg`);
      this.renderer.setStyle(img, 'width', '24px');
    }

    const infoDiv = this.renderer.createElement('div');
    this.renderer.addClass(infoDiv, 'd-flex');
    this.renderer.addClass(infoDiv, 'ml-1');
    this.renderer.addClass(infoDiv, 'w-100');
    this.renderer.setProperty(infoDiv, 'innerHTML', this.updateDropdownContent(account));
    if (this.showFlag) {
      this.renderer.appendChild(accountDiv, img);
    }
    this.renderer.appendChild(accountDiv, infoDiv);

    this.renderer.appendChild(section, accountDiv);

    return section;
  }

  clearDropdown() {
    while (this.dropdown.firstChild) {
      this.dropdown.removeChild(this.dropdown.firstChild);
    }
    if (this.footerView) {
      this.appRef.detachView(this.footerView);
      try {
        this.footerView.destroy();
      } catch (e) {
        console.error('Error destroying footer view', e);
      }
      this.footerView = null;
    }
    if (this.footerContainer) {
      try {
        this.renderer.removeChild(this.dropdown, this.footerContainer);
      } catch (e) {
        console.error('Error destroying footer view', e);
      }
      this.footerContainer = null;
    }
  }

  private get isModal() {
    return getQueryParamValueByKey('isModal');
  }

  createDropdown() {
    this.dropdown = this.renderer.createElement('div');
    this.renderer.addClass(this.dropdown, 'account-dropdown');
    const borderRadiusClass = getComputedStyle(this.document.documentElement).getPropertyValue(
      '--inputFieldBorderRadiusClass',
    );
    if (borderRadiusClass) {
      this.renderer.addClass(this.dropdown, borderRadiusClass);
    }
    this.renderer.setStyle(this.dropdown, 'position', 'absolute');
    this.renderer.setStyle(this.dropdown, 'cursor', 'pointer');
    if (this.isModal) {
      // Use 100000 z-index value here, as page as modal component has z-index with 10000 value
      this.renderer.setStyle(this.dropdown, 'z-index', '100000');
    }

    if (this.dropdownListing && this.dropdownListing.length > 0) {
      this.dropdownListing.forEach((account) => {
        const section = this.createAccountSection(account);
        this.renderer.appendChild(this.dropdown, section);
      });
    } else {
      const emptyEl = this.renderer.createElement('div');
      this.renderer.addClass(emptyEl, 'no-data-found');
      const safeHtml =
        this.sanitizer.sanitize(SecurityContext.HTML, this.safeHtmlPipe.transform(this.noRecordsTemplate)) ??
        '';
      this.renderer.setProperty(emptyEl, 'innerHTML', safeHtml);
      this.renderer.appendChild(this.dropdown, emptyEl);
    }

    // Render footer template into dropdown if provided
    if (this.footerTemplate) {
      const shouldFloat =
        this.footerFloatingThreshold == null ||
        (this.dropdownListing && this.dropdownListing.length > this.footerFloatingThreshold);

      this.footerContainer = this.renderer.createElement('div');
      this.renderer.addClass(this.footerContainer, 'p-1');
      if (shouldFloat) this.renderer.addClass(this.footerContainer, 'footer-sticky');

      // create and attach embedded view
      try {
        this.footerView = this.footerTemplate.createEmbeddedView({});
        this.appRef.attachView(this.footerView);
        this.footerView.rootNodes.forEach((node) => this.renderer.appendChild(this.footerContainer, node));
        this.renderer.appendChild(this.dropdown, this.footerContainer);
      } catch (e) {
        console.error('Failed to render footerTemplate into dropdown', e);
      }
    }
    if (!this.isScrollable) {
      this.renderer.setStyle(this.dropdown, 'max-height', 'unset');
      this.renderer.setStyle(this.dropdown, 'height', 'auto');
    } else {
      this.renderer.addClass(this.dropdown, 'show-scrollbar');
      this.renderer.setStyle(this.dropdown, 'overflow-y', 'auto');
      this.renderer.setStyle(this.dropdown, 'overflow-x', 'hidden');
      this.setDropdownListMaxheight(this.dropdown);
    }
  }

  @HostListener('click')
  toggleDropdown() {
    this.isOpen = !this.isOpen;
    this.dropdownOpened.emit(this.isOpen);
    this.setPosition(this.isOpen);
  }

  setPosition(isOpen: boolean) {
    if (!isOpen) {
      this.removeDropdownFromBody();
      return;
    }

    // Append to body first (off-screen) so we can measure accurate height
    this.renderer.setStyle(this.dropdown, 'visibility', 'hidden');
    this.renderer.appendChild(this.document.body, this.dropdown);

    const rect = this.el.nativeElement.getBoundingClientRect();

    // Determine the actual direction to use
    const direction = this.getActualMenuDirection(rect);

    // Position the dropdown based on direction
    this.positionDropdown(rect, direction);

    this.renderer.setStyle(this.dropdown, 'left', `${rect.left + window.scrollX}px`);
    if (this.isModal) {
      this.renderer.setStyle(this.dropdown, 'right', '');
      this.renderer.setStyle(this.dropdown, 'width', `${rect.width}px`);
    } else {
      this.renderer.setStyle(this.dropdown, 'width', `${rect.width}px`);
    }

    // Make visible after positioning is complete
    this.renderer.setStyle(this.dropdown, 'visibility', 'visible');
  }

  public onDocumentClick = (event: MouseEvent) => {
    const target = event.target as Node;
    // Check if the click happened inside the host(dropdown input field) element
    const clickedInsideHost = this.el.nativeElement.contains(target);
    // Check if the click happened inside the dropdown appended to the body
    const clickedInsideDropdown = this.dropdown?.contains(target);
    // Close the dropdown if the click was outside both the host(dropdown input field) and the dropdown
    if (!clickedInsideHost && !clickedInsideDropdown && this.isOpen) {
      this.toggleDropdown(); // close the dropdown
    }
  };

  /**
   * Removes the dropdown element from the document body.
   *
   * The dropdown is temporarily appended to <body> when it is opened so that
   * it can be positioned independently of its parent container and avoid
   * being clipped by parent elements with overflow restrictions.
   *
   * This method is safe to call even when the dropdown is already detached.
   */
  private removeDropdownFromBody() {
    if (this.dropdown) {
      this.renderer.removeChild(this.document.body, this.dropdown);
    }
  }

  ngOnDestroy() {
    // Remove the dropdown from the body when the directive is destroyed
    this.removeDropdownFromBody();
    this.document.removeEventListener('click', this.onDocumentClick);
    if (this.footerView) {
      try {
        this.appRef.detachView(this.footerView);
        this.footerView.destroy();
      } catch (e) {
        // ignore
      }
      this.footerView = null;
    }
  }

  /**
   * Determines the actual menu direction based on the dropdownMenuDirection setting and viewport space
   * @param rect - The bounding rectangle of the host element
   * @returns The actual direction to use (UP or DOWN)
   */
  private getActualMenuDirection(rect: DOMRect): DropdownMenuDirection {
    if (this.dropdownMenuDirection === DropdownMenuDirection.UP) {
      return DropdownMenuDirection.UP;
    }

    if (this.dropdownMenuDirection === DropdownMenuDirection.DOWN) {
      return DropdownMenuDirection.DOWN;
    }

    // AUTO mode: calculate based on available space
    const viewportHeight = window.innerHeight;
    const spaceBelow = viewportHeight - rect.bottom;
    const spaceAbove = rect.top;

    // Get actual or estimated dropdown height
    const dropdownHeight = this.getDropdownHeight();

    // If there's more space above and not enough space below, open upward
    if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
      return DropdownMenuDirection.UP;
    }

    // Default to opening downward
    return DropdownMenuDirection.DOWN;
  }

  /**
   * Gets the actual or estimated height of the dropdown
   * @returns Height in pixels
   */
  private getDropdownHeight(): number {
    // If dropdown exists and has been rendered, use actual height
    if (this.dropdown?.offsetHeight) {
      return this.dropdown.offsetHeight;
    }

    // Fallback to estimation
    const defaultItemHeight = 60;
    const footerHeight = this.footerTemplate ? 40 : 0;
    const itemsHeight = this.visibleItemsCount * defaultItemHeight;
    const peekHeight = this.isScrollPeekEnabled ? defaultItemHeight / 2 : 0;

    return itemsHeight + peekHeight + footerHeight;
  }

  /**
   * Positions the dropdown above or below the host element based on direction
   * @param rect - The bounding rectangle of the host element
   * @param direction - The direction to position the dropdown (UP or DOWN)
   */
  private positionDropdown(rect: DOMRect, direction: DropdownMenuDirection): void {
    if (direction === DropdownMenuDirection.UP) {
      // Position above the host element
      const dropdownHeight = this.getDropdownHeight();
      const top = rect.top + window.scrollY - dropdownHeight;
      this.renderer.setStyle(this.dropdown, 'top', `${top}px`);
    } else {
      // Position below the host element (DOWN)
      const top = rect.bottom + window.scrollY;
      this.renderer.setStyle(this.dropdown, 'top', `${top}px`);
    }
  }

  private setDropdownListMaxheight(dropdownElement: HTMLElement) {
    const itemsCount = dropdownElement.childElementCount;
    if (itemsCount === 0) {
      this.renderer.setStyle(dropdownElement, 'max-height', 'auto');
      return;
    }
    if (itemsCount < this.visibleItemsCount) {
      this.visibleItemsCount = itemsCount;
    }
    const firstElementInsideParent = dropdownElement.firstChild;
    const childElementHeight =
      firstElementInsideParent && (firstElementInsideParent as HTMLElement)?.offsetHeight !== 0
        ? (firstElementInsideParent as HTMLElement).offsetHeight
        : 60;
    let heightToApply = this.visibleItemsCount * childElementHeight;
    //if scrollpeek is enabled then add half of the child element height to make next item partially visible
    if (this.isScrollPeekEnabled) {
      heightToApply += childElementHeight / 2;
    }
    // Set max-height to show only the specified number of items
    this.renderer.setStyle(dropdownElement, 'max-height', `${heightToApply}px`);
  }
}
